/**
 * THE CLIENT'S OWN CLOCK — one definition, asked by both surfaces that print it.
 *
 * 🔴 **W20(e) HALF 3.** Theirs prints `9:48 AM - Europe/Athens` beside a home glyph on the
 * client page, and `docs/NEXT-WORK.md` W20(e) records why it is worth copying: *it is the one
 * thing on that board that tells a coach whether now is a reasonable moment to message
 * somebody.* Ours prints it in two places — the client page's setup card and the Inbox rail's
 * line under a client's name.
 *
 * ⚠️ **IT IS ONE FUNCTION BECAUSE IT IS TWO SURFACES.** The codex's defining bug class is two
 * components, each internally correct, naming one thing differently with nothing to catch the
 * disagreement — and its twenty-fourth instance was found by RENDERING two spellings of one
 * section name a line apart. Two clocks formatted independently would agree on the day they
 * were written and diverge on the first edit to either. So: one formatter, two callers.
 *
 * ── THE RULE THAT DECIDES EVERY BRANCH BELOW ────────────────────────────────────────────────
 *
 * 🔴 **AN ABSENT ZONE PRINTS NOTHING.** Not the coach's own time under the client's name, not
 * `—`, not `UTC`. The whole value of the line is deciding whether NOW is a reasonable moment to
 * message somebody, so a wrong clock does not fail to answer that question — it answers it
 * confidently and wrongly, which is strictly worse than silence. And a dash where a clock goes
 * reads as a clock that is BROKEN, which sends a coach to us rather than to their client.
 *
 * ⚠️ That is why this returns `null` rather than a string, and why there is no `?? "—"` at
 * either call site. `emptyMark`'s em dash is right for a CELL in a table, where the column
 * promises a value and its absence is the answer; it is wrong here, where the line simply does
 * not exist.
 *
 * ⚠️ **AND AN UNRESOLVABLE ZONE IS AN ABSENT ZONE.** The column is `text` with no CHECK on
 * purpose (the tz database gains and loses names between iOS releases, so a constraint written
 * today would start refusing a correct write from a future phone). The cost of that freedom is
 * that a stored string may not resolve here — an old name this browser's ICU does not carry, or
 * a value from a build we have not seen. `Intl.DateTimeFormat` throws `RangeError` on one, and
 * an uncaught throw in a render takes the whole page down. So it is caught, and it lands in the
 * same honest-empty case as a null.
 */

export type LocalClock = {
  /** The client's wall time, e.g. `9:48 AM`. */
  readonly time: string;
  /** The IANA name, exactly as stored — `Europe/Athens`. */
  readonly zone: string;
};

/**
 * The client's local time, or `null` when we genuinely do not know it.
 *
 * `at` is passed in rather than read from the clock inside, for the reason every date rule in
 * this app is written that way: a function that reads `new Date()` itself cannot be asked what
 * it does at a particular moment, so its most interesting behaviour is the part no test can
 * reach.
 */
export function localClock(
  zone: string | null | undefined,
  at: Date,
): LocalClock | null {
  // An empty or whitespace-only string is not a zone. It should never be stored — the phone
  // writes an identifier it has already checked against `TimeZone.knownTimeZoneIdentifiers` —
  // but a column with no CHECK can hold one, and `Intl` treats `""` as "use the local zone",
  // which is the coach's own time wearing the client's name. That is the exact wrong answer.
  const trimmed = typeof zone === "string" ? zone.trim() : "";
  if (trimmed === "") return null;

  // A date that is not a date formats as `Invalid Date` rather than throwing — a plausible
  // string in a place a coach reads a number, which is the shape this file exists to refuse.
  if (Number.isNaN(at.getTime())) return null;

  try {
    const time = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: trimmed,
    }).format(at);
    return { time, zone: trimmed };
  } catch {
    // RangeError: an identifier this runtime's ICU does not carry. Honest empty, never a guess.
    return null;
  }
}

/**
 * 🔴 W72 STEP 7 — ONE CLOCK PHRASE FOR BOTH SURFACES: `2:36 PM in Athens` on the Profile card, `2:36 PM in Athens · Thursday`
 * in the thread head. `localClockLine`'s `9:48 AM · Europe/Athens` is GONE — two spellings of one fact on two screens
 * (step 6's finding 2) is the codex's defining bug class, one level up. The middle dot still never becomes their hyphen.
 * ⚠️ THE CITY IS THE ZONE'S LAST SEGMENT (`America/Argentina/Buenos_Aires` → `Buenos Aires`) — a NAME, for the reason this
 * file already gives against an offset: it survives summer time.
 * ⚠️ THE WEEKDAY IS THEIRS, in their zone, and only where it is asked for (the thread head, `withWeekday`): at 1 AM in
 * Athens it is already Friday there while the coach in London is on Thursday, and "is it their rest day" is asked in
 * THEIR week.
 * ⚠️ NO "Week 5": the web has no block position for a coached client (the old roster caption's finding, step 1; `thisWeekBoard.ts` W126 note).
 */
export function localClockPhrase(zone: string | null | undefined, at: Date, withWeekday = false): string | null {
  const c = localClock(zone, at);
  if (c === null) return null;
  const city = (c.zone.split("/").pop() ?? c.zone).replace(/_/g, " ");
  if (!withWeekday) return `${c.time} in ${city}`;
  let weekday = "";
  try {
    weekday = new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: c.zone }).format(at);
  } catch {
    weekday = "";
  }
  return weekday === "" ? `${c.time} in ${city}` : `${c.time} in ${city} · ${weekday}`;
}
